import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import type { components } from "./zz_schema";

import { useAuth } from "@/auth/auth";

export type NodePoolRow = components["schemas"]["NodePool"];
export type NodePoolMac = components["schemas"]["NodePoolMac"];
export type NodePoolCreate = components["schemas"]["NodePoolCreate"];
export type NodePoolUpdate = components["schemas"]["NodePoolUpdate"];
export type Region = components["schemas"]["Region"];

const nodePoolsKey = (organizationId?: string) =>
	organizationId ? `/v1/organizations/${organizationId}/node_pools` : null;

// A pool's instances and Macs follow its usage, so the list refreshes.
export function useOrganizationNodePools(organizationId?: string) {
	const auth = useAuth();
	return useSWR(
		nodePoolsKey(organizationId),
		() => {
			if (!organizationId) return undefined;
			return auth.client.GET("/v1/organizations/{organizationId}/node_pools", {
				params: { path: { organizationId } },
			});
		},
		{ refreshInterval: 15_000 },
	);
}

// useRegions lists the regions with the base pools each offers node pools.
export function useRegions() {
	const auth = useAuth();
	return useSWR("/regions", () => auth.client.GET("/regions"));
}

export function useCreateOrganizationNodePool(organizationId?: string) {
	const auth = useAuth();
	return useSWRMutation(
		nodePoolsKey(organizationId),
		(_, { arg }: { arg: NodePoolCreate }) => {
			if (!organizationId) return undefined;
			return auth.client.POST("/v1/organizations/{organizationId}/node_pools", {
				params: { path: { organizationId } },
				body: arg,
			});
		},
	);
}

export function useUpdateOrganizationNodePool(organizationId?: string) {
	const auth = useAuth();
	return useSWRMutation(
		nodePoolsKey(organizationId),
		(_, { arg }: { arg: { id: string; changes: NodePoolUpdate } }) => {
			if (!organizationId) return undefined;
			return auth.client.PATCH(
				"/v1/organizations/{organizationId}/node_pools/{id}",
				{
					params: { path: { organizationId, id: arg.id } },
					body: arg.changes,
				},
			);
		},
	);
}

export function useDeleteOrganizationNodePool(organizationId?: string) {
	const auth = useAuth();
	return useSWRMutation(
		nodePoolsKey(organizationId),
		(_, { arg }: { arg: { id: string } }) => {
			if (!organizationId) return undefined;
			return auth.client.DELETE(
				"/v1/organizations/{organizationId}/node_pools/{id}",
				{
					params: { path: { organizationId, id: arg.id } },
				},
			);
		},
	);
}
